import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { Auth, authState, onAuthStateChanged } from '@angular/fire/auth';
import { firstValueFrom, Observable } from 'rxjs';

import { AppState } from '../app.state';
import * as AuthActions from './auth.actions';
import { AuthState, IUserData, SupportedWallets } from './auth.state';
import { filter, pluck, map, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { doc, Firestore } from '@angular/fire/firestore';
import { ProfileStateDispatcher } from '../profile/profile.dispatcher';
import { FireSubService } from '../../firebase/fireSub.service';
import { SopoPopupService } from 'src/app/sopo-popup/service/sopo-popup.service';
import { IPersona, PersonaVerificationStatus } from '@soposopo/sopo-models';
import Persona from 'persona';
import * as Sentry from "@sentry/angular";
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class AuthStoreDispatcher {
    constructor(
        private store: Store<AppState>,
        private authService: Auth,
        private firestore: Firestore,
        private profileStateServcie: ProfileStateDispatcher,
        private fireSubService: FireSubService,
        private sopoPopupService: SopoPopupService,
        private router: Router
    ) {
        onAuthStateChanged(
            this.authService,
            (authenticatedUser) => {
                if (this.router.url.includes('desktop-login')) return;
                if (authenticatedUser) {
                    const lastSignInTime = authenticatedUser.metadata.lastSignInTime ? new Date(authenticatedUser.metadata.lastSignInTime).getTime() : new Date().getTime();
                    const creationTime = authenticatedUser.metadata.creationTime ? new Date(authenticatedUser.metadata.creationTime).getTime() : new Date().getTime();
                    this.store.dispatch(
                        AuthActions.authenticatedAction({
                            userData: {
                                displayName: authenticatedUser.displayName,
                                email: authenticatedUser.email,
                                phoneNumber: authenticatedUser.phoneNumber,
                                photoURL: authenticatedUser.photoURL,
                                providerId: authenticatedUser.providerId,
                                uid: authenticatedUser.uid,
                                lastSignInTime,
                                creationTime,
                                isAnonymous: authenticatedUser.isAnonymous,
                            },
                        }),
                    );
                    Sentry.setUser({
                        id: authenticatedUser.uid,
                        email: authenticatedUser.email
                    });
                } else {
                    this.store.dispatch(AuthActions.notAuthenticatedAction());
                }
            },
            (error) => {
                this.store.dispatch(AuthActions.notAuthenticatedAction());
            },
        );

        if (localStorage.getItem('hasWeakPassword')) {
            this.store.dispatch(AuthActions.setWeakPasswordAction({ isWeak: true }))
        }
    }

    getUserDocReference(Id: string) {
        return doc(this.firestore, `users/${Id}`);
    }

    public login(email: string, password: string, hasWeakPassword = false): void {
        this.store.dispatch(AuthActions.loginWithEmailAction(email, password, hasWeakPassword));
    }

    public signup(email: string, password: string, inviteCode: string | null): void {
        this.store.dispatch(AuthActions.signupAction(email, password, inviteCode));
    }

    public resetPassword(email: string): void {
        this.store.dispatch(AuthActions.resetPasswordAction(email));
    }

    public async logout(isDelete = false, inviteCode?: string, logInOnOtherDevice?: boolean): Promise<void> {
        const isAuthenticated$ = authState(this.authService).pipe(map((user) => (user ? true : false)))
        const isAuthenticated = await firstValueFrom(isAuthenticated$);
        if (!isAuthenticated) return;
        this.fireSubService.unsubscribeAllSubs();
        this.profileStateServcie.unsubscribeProfile();
        await this.sopoPopupService.clearAllViewContainer();
        this.store.dispatch(AuthActions.logoutAction({isDelete, inviteCode, logInOnOtherDevice}));
        await firstValueFrom(isAuthenticated$.pipe(filter((isAuth) => !isAuth)));
    }

    public signInAnonymously(): void {
        this.store.dispatch(AuthActions.loginAsGuestAction());
    }

    public loginWithFacebook(inviteCode: string | null): void {
        this.store.dispatch(AuthActions.loginWithFacebookAction(inviteCode));
    }

    public loginWithGoogle(inviteCode: string | null): void {
        this.store.dispatch(AuthActions.loginWithGoogleAction(inviteCode));
    }

    public loginWithApple(inviteCode: string | null): void {
        this.store.dispatch(AuthActions.loginWithAppleAction(inviteCode));
    }

    public nonAnonymousLogin(): void {
        this.store.dispatch(AuthActions.nonAnonymousLogin());
    }

    public loginWithWallet(inviteCode: string | null, wallet: SupportedWallets): void {
        this.store.dispatch(AuthActions.loginWithWalletAction(inviteCode, wallet));
    }

    public setIsRegistration(isRegistration: boolean | null): void {
        this.store.dispatch(AuthActions.setIsRegistrationAction({ isRegistration }));
    }

    /**  Returns the ID of the authenticated client */
    public getMyID(): Promise<string> {
        return firstValueFrom(this.getUserData()
            .pipe(
                map((userData) => (userData ? userData.uid : '')),
                filter((uid) => uid.length > 0)
            ));
    }

    public getState(): Observable<AuthState> {
        return this.store.select((state: AppState) => state.auth).pipe(filter((authState) => authState != null));
    }

    public isAuthenticated(): Observable<boolean> {
        return this.store
            .select((state: AppState) => state.auth)
            .pipe(
                filter((authState) => authState != null),
                map((authState) => authState.isAuthenticated),
            );
    }

    public getIsClient(id: string): Observable<boolean> {
        return this.getState().pipe(
            map((data) => data.userData),
            filter((userData) => userData != null),
            map((user) => {
                return user ? id === user.uid : false;
            }),
        );
    }

    public amIClient(): Observable<boolean> {
        return this.getUserData().pipe(
            filter((userData) => userData !== null),
            map((userData) => (userData ? userData.uid : '')),
            switchMap((uid) => this.getIsClient(uid)),
        );
    }

    public getUserData(): Observable<IUserData | null> {
        return this.getState().pipe(
            filter((authState) => authState.userData != null),
            pluck('userData'),
        );
    }

    public isAnonymousObservable(): Observable<boolean> {
        return this.store
            .select((state: AppState) => state.auth)
            .pipe(
                filter((authState) => authState != null),
                map(authState => authState.userData),
                map((userData) => userData ? userData.isAnonymous : false),
                distinctUntilChanged(),
            )
    }

    public async isAnonymous(): Promise<boolean> {
        return firstValueFrom(this.isAnonymousObservable());
    }

    public setWeakPassword(isWeak: boolean) {
        if (!isWeak) localStorage.removeItem('hasWeakPassword');
        this.store.dispatch(AuthActions.setWeakPasswordAction({ isWeak }));
    }

    public getIdToken(): Promise<string> {
        return this.authService.currentUser.getIdToken();
    }

    public async getAuthEmail(): Promise<string> {
        return firstValueFrom(this.getUserData().pipe(
            map(userData => userData.email),
        ));
    }
}

